const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

function authHeaders(json = false): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  };
}

async function asJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(typeof body.detail === 'string' ? body.detail : `Request failed (${response.status})`);
  }
  return response.json();
}

export interface LockedSoundbedVersion {
  version: number;
  source_page_url: string;
  source_download_url: string;
  source_audio_sha256: string;
  source_audio_bytes: number;
  source_content_type: string;
  duration_seconds: number;
  sample_rate_hz: number;
  channels: number;
  baseline_lufs: number | null;
  baseline_true_peak_dbtp: number | null;
  license_spdx_id: string;
  license_url: string;
  attribution_required: boolean;
  attribution_text: string | null;
  territory_scope: string;
  rights_review_state: string;
  technical_review_state: string;
  source_evidence: Record<string, unknown>;
}

export interface LockedSoundbedAsset {
  asset_id: string;
  title: string;
  creator_name: string;
  asset_state: string;
  versions: LockedSoundbedVersion[];
}

export interface LockedSoundbedRegistry {
  schema_version: string;
  fingerprint: string;
  assets: LockedSoundbedAsset[];
}

export interface SoundbedVersionReview {
  version: number;
  source_page_url: string;
  source_audio_sha256: string;
  license_spdx_id: string;
  license_url: string;
  attribution_text: string | null;
  territory_scope: string;
  rights_review_state: string;
  technical_review_state: string;
  storage_ready: boolean;
  rights_evidence_sha256: string;
  technical_evidence_sha256: string;
}

export interface SoundbedReviewAsset {
  asset_id: string;
  title: string;
  creator_name: string;
  asset_state: string;
  versions: SoundbedVersionReview[];
}

export interface SoundbedArcCue {
  segment_id: number;
  energy: number;
}

export interface SoundbedAssignmentOption {
  story_id: string;
  title: string;
  performance_version: number;
  arc_cues: SoundbedArcCue[];
}

export interface SoundbedAssignment {
  assignment_id: string;
  assignment_state: string;
  story_id: string;
  story_title: string;
  performance_version: number;
  asset_id: string;
  asset_title: string;
  asset_version: number;
  mix_profile_version: string;
  current_mix_profile_version: string;
  mix_profile_history: Array<{
    mix_profile_version: string;
    reason_code: string;
    revised_at: string;
  }>;
  arc_cues: SoundbedArcCue[];
  created_at: string;
}

export interface InternalSoundbedReview {
  assignment_id: string;
  render_id: string;
  voice_id: string;
  listener_id: string | null;
  status: string;
  output_seconds: number | null;
  created_at: string;
}

export async function fetchLockedSoundbedRegistry() {
  return asJson<LockedSoundbedRegistry>(
    await fetch(`${API_BASE_URL}/admin/soundbeds/registry`, { headers: authHeaders() })
  );
}

export async function fetchSoundbedReviews() {
  return asJson<SoundbedReviewAsset[]>(
    await fetch(`${API_BASE_URL}/admin/soundbeds`, { headers: authHeaders() })
  );
}

export async function fetchSoundbedAssignmentOptions() {
  return asJson<SoundbedAssignmentOption[]>(
    await fetch(`${API_BASE_URL}/admin/soundbeds/assignment-options`, { headers: authHeaders() })
  );
}

export async function fetchSoundbedAssignments() {
  return asJson<SoundbedAssignment[]>(
    await fetch(`${API_BASE_URL}/admin/soundbeds/assignments`, { headers: authHeaders() })
  );
}

export async function fetchInternalSoundbedReviews() {
  return asJson<InternalSoundbedReview[]>(
    await fetch(`${API_BASE_URL}/renders/internal-soundbed-review`, { headers: authHeaders() })
  );
}

export async function intakeLockedSoundbed(
  assetId: string,
  registryFingerprint: string,
  approvalReference: string,
) {
  return asJson<Record<string, unknown>>(
    await fetch(`${API_BASE_URL}/admin/soundbeds/registry/intake`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({
        asset_id: assetId,
        registry_fingerprint: registryFingerprint,
        approval_reference: approvalReference,
      }),
    })
  );
}

export async function approveSoundbedRights(
  assetId: string,
  version: number,
  evidenceSha256: string,
) {
  return asJson<Record<string, unknown>>(
    await fetch(`${API_BASE_URL}/admin/soundbeds/${assetId}/versions/${version}/rights-review`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({ decision: 'approve', evidence_sha256: evidenceSha256 }),
    })
  );
}

export async function uploadAndApproveSoundbedSource(
  assetId: string,
  version: number,
  evidenceSha256: string,
  file: File,
) {
  const form = new FormData();
  form.append('evidence_sha256', evidenceSha256);
  form.append('file', file);
  return asJson<Record<string, unknown>>(
    await fetch(`${API_BASE_URL}/admin/soundbeds/${assetId}/versions/${version}/source`, {
      method: 'POST',
      headers: authHeaders(),
      body: form,
    })
  );
}

export async function fetchAndApproveSoundbedSource(
  assetId: string,
  version: number,
  evidenceSha256: string,
) {
  return asJson<Record<string, unknown>>(
    await fetch(`${API_BASE_URL}/admin/soundbeds/${assetId}/versions/${version}/source/fetch`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({ evidence_sha256: evidenceSha256 }),
    })
  );
}

export async function stageSoundbedAssignment(
  storyId: string,
  assetId: string,
  assetVersion: number,
  arcCues: SoundbedArcCue[],
) {
  return asJson<{ assignment_id: string; assignment_state: string; changed: boolean }>(
    await fetch(`${API_BASE_URL}/admin/soundbeds/assignments`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({
        story_id: storyId,
        asset_id: assetId,
        asset_version: assetVersion,
        arc_cues: arcCues,
      }),
    })
  );
}

export async function reviseSoundbedAssignmentMixProfile(
  assignmentId: string,
  confirmedPreviousVersion: string,
) {
  return asJson<{
    assignment_id: string;
    assignment_state: string;
    mix_profile_version: string;
    changed: boolean;
  }>(
    await fetch(`${API_BASE_URL}/admin/soundbeds/assignments/${assignmentId}/revise-mix-profile`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({
        confirmed_previous_version: confirmedPreviousVersion,
        reason_code: 'intelligibility_gate_failed',
      }),
    })
  );
}

export async function enqueueInternalSoundbedReview(
  storyId: string,
  voiceId: string,
  assignmentId: string,
  listenerId: string,
) {
  const params = new URLSearchParams({
    story_id: storyId,
    voice_id: voiceId,
    assignment_id: assignmentId,
    listener_id: listenerId,
  });
  return asJson<{ job_id: string | null; status: 'queued' | 'already_rendering'; render_id?: string }>(
    await fetch(`${API_BASE_URL}/renders/internal-soundbed-review?${params}`, {
      method: 'POST',
      headers: authHeaders(),
    })
  );
}

export interface SoundbedAssignmentListenReview {
  render_id: string;
  listened_seconds: number;
  completion_ratio: number;
  intro_listened: boolean;
  goodnight_listened: boolean;
  voice_only_compared: boolean;
  bookends_foregrounded: boolean;
  ended_in_silence: boolean;
}

export async function approveSoundbedAssignment(
  assignmentId: string,
  review: SoundbedAssignmentListenReview,
) {
  return asJson<{
    assignment_id: string;
    assignment_state: 'approved';
    changed: boolean;
  }>(
    await fetch(`${API_BASE_URL}/admin/soundbeds/assignments/${assignmentId}/approve`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify(review),
    })
  );
}
